// frontend/src/components/plot/MetricCharts.js
import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Brush, Customized
} from 'recharts';
import { getLighterColor } from '../../utils/colorUtils';

// ============================================================================
// SECTION & PANEL ORDERING CONFIGURATION
// ============================================================================

export const METRIC_PANEL_ORDER = {
  crop: [
    'crop:degree_days',
    'crop:degree_hours',
    'pest:degree_days',
    'pest:degree_hours',
    'friend:degree_days',
    'friend:degree_hours'
  ],
  realtime: [
    'vpd',
    'lwd',
    'fuel_moisture',
    'heatindex'
  ],
  weather: [
    'temperature',
    'dewpoint',
    'humidity',
    'pressure',
    'precipitation',
    'irrad',
    'wind'
  ],
  telemetry: [
    'battery',
    'battery_voltage',
    'rssi',
    'wifi',
    'disk',
    'disk_free',
    'disk_free_percent'
  ],
  custom: []
};

export const sortCategoryMetrics = (categoryKey, metrics = []) => {
  const normCategory = String(categoryKey).toLowerCase();
  const categoryOrderKey = Object.keys(METRIC_PANEL_ORDER).find((key) => normCategory.includes(key));
  const preferredOrder = METRIC_PANEL_ORDER[categoryOrderKey] || [];

  return [...metrics].sort((a, b) => {
    const keyA = typeof a === 'string' ? a : (a.type || a.id || '');
    const keyB = typeof b === 'string' ? b : (b.type || b.id || '');

    const getRank = (key) => {
      const exactIdx = preferredOrder.indexOf(key);
      if (exactIdx !== -1) return exactIdx;
      const prefixIdx = preferredOrder.findIndex((prefix) => key.startsWith(prefix));
      if (prefixIdx !== -1) return prefixIdx;
      return 999;
    };

    const rankA = getRank(keyA);
    const rankB = getRank(keyB);

    if (rankA !== rankB) return rankA - rankB;
    return keyA.localeCompare(keyB);
  });
};

// ============================================================================
// METRIC AXIS CONFIGURATION
// ============================================================================

const METRIC_AXIS_CONFIG = {
  pressure: { mode: 'tight', deltaPadding: 2 },
  humidity: { mode: 'fixed', min: 0, max: 100 },
  fuel_moisture: { mode: 'fixed', min: 0, max: 100 },
  precip: { mode: 'zero_based', percentPadding: 0.2 },
  precipitation: { mode: 'zero_based', percentPadding: 0.2 },
  irrad: { mode: 'zero_based', percentPadding: 0.15 },
  wind: { mode: 'zero_based', percentPadding: 0.15 },
  temp: { mode: 'padded', percentPadding: 0.1 },
  vpd: { mode: 'zero_based', percentPadding: 0.1 },
  battery: { mode: 'padded', percentPadding: 0.1 },
  battery_voltage: { mode: 'padded', percentPadding: 0.1 },
  rssi: { mode: 'tight', deltaPadding: 5 },
  wifi: { mode: 'tight', deltaPadding: 5 },
  disk: { mode: 'fixed', min: 0, max: 100 },
  disk_free_percent: { mode: 'fixed', min: 0, max: 100 },
  default: { mode: 'padded', percentPadding: 0.05 }
};

export const getYAxisDomain = (metricId) => {
  const config = METRIC_AXIS_CONFIG[metricId] || METRIC_AXIS_CONFIG.default;

  switch (config.mode) {
    case 'fixed':
      return [config.min, config.max];
    case 'zero_based':
      return [
        0,
        (dataMax) => {
          if (!Number.isFinite(dataMax)) return 'auto';
          const pad = Math.abs(dataMax) * (config.percentPadding || 0.1);
          return Math.ceil(dataMax + pad);
        }
      ];
    case 'tight':
      return [
        (dataMin) => {
          if (!Number.isFinite(dataMin)) return 'auto';
          return Math.floor(dataMin - (config.deltaPadding || 2));
        },
        (dataMax) => {
          if (!Number.isFinite(dataMax)) return 'auto';
          return Math.ceil(dataMax + (config.deltaPadding || 2));
        }
      ];
    case 'padded':
    default:
      return [
        (dataMin) => {
          if (!Number.isFinite(dataMin)) return 'auto';
          const pad = Math.abs(dataMin) * (config.percentPadding || 0.05) || 1;
          return Math.floor(dataMin - pad);
        },
        (dataMax) => {
          if (!Number.isFinite(dataMax)) return 'auto';
          const pad = Math.abs(dataMax) * (config.percentPadding || 0.05) || 1;
          return Math.ceil(dataMax + pad);
        }
      ];
  }
};

// ============================================================================
// UTILITY FUNCTIONS: WIND VECTOR TO SPEED & DIRECTION
// ============================================================================

export function computeWindSpeedAndDirection(u, v) {
  if (u === null || u === undefined || v === null || v === undefined) {
    return { speed: null, direction: null };
  }
  const speed = Math.sqrt(u * u + v * v);
  let dirDeg = Math.atan2(-u, -v) * (180 / Math.PI);
  if (dirDeg < 0) dirDeg += 360;

  return {
    speed: Number(speed.toFixed(1)),
    direction: Number(dirDeg.toFixed(0))
  };
}

export function getWindCompassCardinals(deg) {
  if (deg === null || deg === undefined || isNaN(deg)) return '';
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const val = Math.floor((deg / 22.5) + 0.5);
  return directions[(val % 16)];
}

// ============================================================================
// HELPERS: GROWTH STAGES & DATA FILTERING
// ============================================================================

export function getGrowthStageKey(chartData, metricKey) {
  if (!Array.isArray(chartData) || chartData.length === 0 || !metricKey) return null;
  if (!metricKey.includes('degree_days') && !metricKey.includes('degree_hours')) return null;

  const targetBase = metricKey
    .replace('degree_days', 'growth_stage')
    .replace('degree_hours', 'growth_stage');

  for (let i = 0; i < chartData.length; i++) {
    const record = chartData[i];
    if (record && targetBase in record && record[targetBase] !== undefined) {
      return targetBase;
    }
  }

  for (let i = 0; i < chartData.length; i++) {
    const record = chartData[i];
    if (!record) continue;
    for (const key in record) {
      if (key.startsWith(targetBase) && record[key] !== undefined) {
        return key;
      }
    }
  }

  for (let i = 0; i < chartData.length; i++) {
    const record = chartData[i];
    if (!record) continue;
    const fallbackKey = Object.keys(record).find((k) => k.includes('growth_stage') && record[k] !== undefined);
    if (fallbackKey) return fallbackKey;
  }

  return null;
}

export function computeStageTransitions(chartData, stageKey) {
  if (!Array.isArray(chartData) || chartData.length === 0 || !stageKey) return [];

  const transitions = [];
  let lastStage = null;

  chartData.forEach((point) => {
    const stageValRaw = point[stageKey];
    if (stageValRaw !== undefined && stageValRaw !== null) {
      const stageVal = typeof stageValRaw === 'object'
        ? String(stageValRaw.name || stageValRaw.label || stageValRaw.value || stageValRaw.id || JSON.stringify(stageValRaw))
        : String(stageValRaw);

      if (stageVal !== lastStage) {
        transitions.push({
          x: point.timestamp,
          stage: stageVal,
          label: isNaN(Number(stageVal)) ? stageVal : `Stage ${stageVal}`
        });
        lastStage = stageVal;
      }
    }
  });

  return transitions;
}

const getFilteredChartData = (chartData, forecastVisible) => {
  if (!forecastVisible && Array.isArray(chartData) && chartData.length > 0) {
    let maxHistTimestamp = 0;
    chartData.forEach((pt) => {
      const hasHist = Object.keys(pt).some((k) => k.endsWith('_hist') && pt[k] !== null && pt[k] !== undefined);
      if (hasHist && pt.timestamp > maxHistTimestamp) {
        maxHistTimestamp = pt.timestamp;
      }
    });
    if (maxHistTimestamp > 0) {
      return chartData.filter((pt) => pt.timestamp <= maxHistTimestamp);
    }
  }
  return chartData;
};

const formatXAxisTick = (ts, localTimeEnabled = true) => {
  if (!ts) return '';
  if (typeof ts === 'string') return ts;
  const date = new Date(ts);
  if (isNaN(date.getTime())) return String(ts);

  const m = date.getMonth() + 1;
  const d = date.getDate();
  const hh = String(localTimeEnabled ? date.getHours() : date.getUTCHours()).padStart(2, '0');
  return `${m}/${d} ${hh}:00`;
};

export function groupAccumulationMetricsBySpecies(metrics = []) {
  const groups = {};
  metrics.forEach((m) => {
    if (m.type.includes('growth_stage')) return;
    let modelKey = 'default';
    if (m.type.includes('_')) {
      const parts = m.type.split('_');
      modelKey = parts[parts.length - 1];
      if (modelKey === 'days' || modelKey === 'hours' || modelKey === 'degree') {
        modelKey = 'default';
      }
    }
    if (!groups[modelKey]) {
      groups[modelKey] = {
        name: modelKey === 'default' ? 'Accumulation & Phenology' : modelKey.replace(/_/g, ' ').toUpperCase(),
        metrics: []
      };
    }
    groups[modelKey].metrics.push(m);
  });
  return Object.values(groups);
}

// ============================================================================
// CUSTOM TOOLTIP COMPONENT FOR MULTI-LINE LABELS
// ============================================================================

const CustomTooltip = ({ active, payload, label, localTimeEnabled }) => {
  if (!active || !payload || payload.length === 0) return null;

  const timeStr = label
    ? (localTimeEnabled ? new Date(label).toLocaleString() : new Date(label).toUTCString())
    : '';

  return (
    <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', padding: '12px', borderRadius: '8px', fontSize: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', maxWidth: '280px' }}>
      <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>
        {timeStr}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {payload.map((item, idx) => {
          const parts = String(item.name || '').split('\n');
          const metricLabel = parts[0] || item.dataKey;
          const deviceName = parts[1] || '';

          return (
            <div key={`tip-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, display: 'inline-block', marginTop: '3px', flexShrink: 0 }} />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>
                  {metricLabel}: <strong>{item.value}</strong>
                </span>
                {deviceName && (
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    {deviceName}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// CHART COMPONENTS
// ============================================================================

export function AccumulationPhenologyChart({ speciesGroup, chartData, visibleDevices, forecastVisible = true, localTimeEnabled = true }) {
  const filteredData = useMemo(() => getFilteredChartData(chartData, forecastVisible), [chartData, forecastVisible]);

  const degreeDaysMetric = speciesGroup.metrics.find((m) => m.type.includes('degree_days'));
  const degreeHoursMetric = speciesGroup.metrics.find((m) => m.type.includes('degree_hours'));

  const sampleMetricType = degreeDaysMetric?.type || degreeHoursMetric?.type || speciesGroup.metrics[0]?.type;
  const stageKey = useMemo(() => getGrowthStageKey(chartData, sampleMetricType), [chartData, sampleMetricType]);
  const stageTransitions = useMemo(() => computeStageTransitions(filteredData, stageKey), [filteredData, stageKey]);

  const currentStage = useMemo(() => {
    if (!filteredData || filteredData.length === 0 || !stageKey) return 'N/A';

    let validPoint = null;
    const now = Date.now();
    for (let i = filteredData.length - 1; i >= 0; i--) {
      const pt = filteredData[i];
      if (pt[stageKey] !== undefined && pt[stageKey] !== null) {
        if (pt.timestamp <= now || !validPoint) {
          validPoint = pt;
          if (pt.timestamp <= now) break;
        }
      }
    }
    if (!validPoint) return 'N/A';

    const rawVal = validPoint[stageKey];
    const val = typeof rawVal === 'object' ? String(rawVal.name || rawVal.label || rawVal.value || rawVal.id) : String(rawVal);
    return isNaN(Number(val)) ? val : `Stage ${val}`;
  }, [filteredData, stageKey]);

  if (!degreeDaysMetric && !degreeHoursMetric) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
        <h5 style={{ margin: 0, color: '#334155', textTransform: 'capitalize' }}>
          {speciesGroup.name}
        </h5>
        <span style={{ fontSize: '11px', fontWeight: 600, background: '#f1f5f9', color: '#334155', padding: '3px 10px', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
          Current Growth Stage: {currentStage}
        </span>
      </div>

      <div style={{ width: '100%', height: '280px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filteredData}>
            <CartesianGrid strokeDasharray="2 2" stroke="#f1f5f9" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(ts) => formatXAxisTick(ts, localTimeEnabled)}
              minTickGap={30}
              tick={{ fontSize: 10 }}
            />
            <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 10 }} domain={['auto', 'auto']} unit=" DD" />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={['auto', 'auto']} unit=" DH" />

            <Tooltip content={<CustomTooltip localTimeEnabled={localTimeEnabled} />} />
            <Brush dataKey="time" height={24} stroke="#94a3b8" fill="#f8fafc" />

            {stageTransitions.map((tr, idx) => (
              <ReferenceLine
                key={`stage-line-${idx}-${tr.stage}`}
                yAxisId="left"
                x={tr.x}
                stroke="#475569"
                strokeWidth={1.5}
                ifOverflow="extendDomain"
                label={{
                  value: tr.label,
                  position: 'insideTopRight',
                  fill: '#475569',
                  fontSize: 11,
                  fontWeight: 600,
                  offset: 5
                }}
              />
            ))}

            {visibleDevices.map((dev) => {
              const fcstColor = getLighterColor(dev.color, 40);
              return (
                <React.Fragment key={dev.id}>
                  {degreeDaysMetric && (
                    <>
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey={`${degreeDaysMetric.type}_${dev.id}_hist`}
                        name={`${degreeDaysMetric.label}\n(${dev.name})`}
                        stroke={dev.color}
                        fill={dev.color}
                        fillOpacity={0.12}
                        strokeWidth={1.5}
                        dot={{ r: 3, shape: 'circle' }}
                        connectNulls={true}
                      />
                      {forecastVisible && (
                        <Area
                          yAxisId="left"
                          type="monotone"
                          dataKey={`${degreeDaysMetric.type}_${dev.id}_fcst`}
                          name={`${degreeDaysMetric.label} forecast\n(${dev.name})`}
                          stroke={fcstColor}
                          fill={fcstColor}
                          fillOpacity={0.06}
                          strokeWidth={1.5}
                          dot={{ r: 3, shape: 'diamond' }}
                          connectNulls={true}
                        />
                      )}
                    </>
                  )}
                  {degreeHoursMetric && (
                    <>
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey={`${degreeHoursMetric.type}_${dev.id}_hist`}
                        name={`${degreeHoursMetric.label}\n(${dev.name})`}
                        stroke={dev.color}
                        strokeWidth={2}
                        dot={{ r: 3, shape: 'circle' }}
                        connectNulls={true}
                      />
                      {forecastVisible && (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey={`${degreeHoursMetric.type}_${dev.id}_fcst`}
                          name={`${degreeHoursMetric.label} forecast\n(${dev.name})`}
                          stroke={fcstColor}
                          strokeWidth={2}
                          dot={{ r: 3, shape: 'diamond' }}
                          connectNulls={true}
                        />
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function WindChart({ chartData, visibleDevices, forecastVisible = true, localTimeEnabled = true }) {
  const enrichedData = useMemo(() => {
    if (!Array.isArray(chartData)) return [];
    return chartData.map((pt) => {
      const copy = { ...pt };
      visibleDevices.forEach((dev) => {
        ['hist', 'fcst'].forEach((mode) => {
          const uKey = `wind_u_${dev.id}_${mode}`;
          const vKey = `wind_v_${dev.id}_${mode}`;
          if (copy[uKey] !== undefined && copy[vKey] !== undefined) {
            const { speed, direction } = computeWindSpeedAndDirection(copy[uKey], copy[vKey]);
            copy[`wind_speed_${dev.id}_${mode}`] = speed;
            copy[`wind_direction_${dev.id}_${mode}`] = direction;
          }
        });
      });
      return copy;
    });
  }, [chartData, visibleDevices]);

  const filteredData = useMemo(() => getFilteredChartData(enrichedData, forecastVisible), [enrichedData, forecastVisible]);

  const hasWindData = visibleDevices.some((dev) =>
    filteredData.some((pt) => pt[`wind_speed_${dev.id}_hist`] !== undefined || pt[`wind_speed_${dev.id}_fcst`] !== undefined)
  );

  if (!hasWindData) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <h5 style={{ margin: '0 0 8px 0', color: '#334155' }}>Wind Speed & Direction</h5>
      <div style={{ width: '100%', height: '280px', position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filteredData}>
            <CartesianGrid strokeDasharray="2 2" stroke="#f1f5f9" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(ts) => formatXAxisTick(ts, localTimeEnabled)}
              minTickGap={30}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              domain={[0, 'auto']}
              unit=" m/s"
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const timeStr = label ? (localTimeEnabled ? new Date(label).toLocaleString() : new Date(label).toUTCString()) : '';
                return (
                  <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', padding: '12px', borderRadius: '8px', fontSize: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', maxWidth: '280px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>
                      {timeStr}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {payload.map((item, idx) => {
                        const parts = String(item.name || '').split('\n');
                        const baseName = parts[0] || item.dataKey;
                        const devName = parts[1] || '';
                        const speedVal = item.value;

                        const dataPt = item.payload;
                        const matchDevId = visibleDevices.find(d => item.dataKey.includes(d.id))?.id;
                        const mode = item.dataKey.includes('_fcst') ? 'fcst' : 'hist';
                        const dirVal = matchDevId ? dataPt[`wind_direction_${matchDevId}_${mode}`] : null;
                        const compass = getWindCompassCardinals(dirVal);

                        return (
                          <div key={`wind-tip-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, display: 'inline-block', marginTop: '3px', flexShrink: 0 }} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 600, color: '#0f172a' }}>
                                {baseName}: <strong>{speedVal} m/s</strong> {dirVal !== null ? `(${dirVal}° ${compass})` : ''}
                              </span>
                              {devName && (
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  {devName}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
            />
            <Brush dataKey="time" height={24} stroke="#94a3b8" fill="#f8fafc" />

            <Customized component={(props) => {
              const { xAxisMap } = props;
              if (!xAxisMap || filteredData.length === 0) return null;
              const xScale = xAxisMap[Object.keys(xAxisMap)[0]]?.scale;
              if (!xScale) return null;

              const step = Math.max(1, Math.floor(filteredData.length / 10));
              const primaryDev = visibleDevices[0];
              if (!primaryDev) return null;

              const arrows = [];
              for (let i = 0; i < filteredData.length; i += step) {
                const pt = filteredData[i];
                const xCoord = xScale(pt.timestamp);
                const dirVal = pt[`wind_direction_${primaryDev.id}_hist`] ?? pt[`wind_direction_${primaryDev.id}_fcst`];
                if (xCoord !== undefined && dirVal !== null && dirVal !== undefined) {
                  arrows.push(
                    <g key={`arrow-${i}`} transform={`translate(${xCoord}, 18)`}>
                      <path
                        d="M 0,-7 L 4,3 L 0,1 L -4,3 Z"
                        fill={primaryDev.color}
                        transform={`rotate(${dirVal})`}
                      />
                    </g>
                  );
                }
              }
              return <g>{arrows}</g>;
            }} />

            {visibleDevices.map((dev) => {
              const fcstColor = getLighterColor(dev.color, 40);
              return (
                <React.Fragment key={dev.id}>
                  <Line
                    type="monotone"
                    dataKey={`wind_speed_${dev.id}_hist`}
                    name={`Wind Speed\n(${dev.name})`}
                    stroke={dev.color}
                    strokeWidth={2}
                    dot={{ r: 3, shape: 'circle' }}
                    connectNulls={true}
                  />
                  {forecastVisible && (
                    <Line
                      type="monotone"
                      dataKey={`wind_speed_${dev.id}_fcst`}
                      name={`Wind Speed forecast\n(${dev.name})`}
                      stroke={fcstColor}
                      strokeWidth={2}
                      dot={{ r: 3, shape: 'diamond' }}
                      connectNulls={true}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function StandardLineChart({ metric, chartData, visibleDevices, forecastVisible = true, localTimeEnabled = true }) {
  const filteredData = useMemo(() => getFilteredChartData(chartData, forecastVisible), [chartData, forecastVisible]);

  return (
    <div style={{ marginBottom: '20px' }}>
      <h5 style={{ margin: '0 0 8px 0', color: '#334155' }}>{metric.label}</h5>
      <div style={{ width: '100%', height: '280px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filteredData}>
            <CartesianGrid strokeDasharray="2 2" stroke="#f1f5f9" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(ts) => formatXAxisTick(ts, localTimeEnabled)}
              minTickGap={30}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              domain={getYAxisDomain(metric.type)}
              unit={metric.unit ? ` ${metric.unit}` : undefined}
            />
            <Tooltip content={<CustomTooltip localTimeEnabled={localTimeEnabled} />} />
            <Brush dataKey="time" height={24} stroke="#94a3b8" fill="#f8fafc" />

            {visibleDevices.map((dev) => {
              const fcstColor = getLighterColor(dev.color, 40);
              return (
                <React.Fragment key={dev.id}>
                  <Line
                    type="monotone"
                    dataKey={`${metric.type}_${dev.id}_hist`}
                    name={`${metric.label}\n(${dev.name})`}
                    stroke={dev.color}
                    strokeWidth={2}
                    dot={{ r: 3, shape: 'circle' }}
                    connectNulls={true}
                  />
                  {forecastVisible && (
                    <Line
                      type="monotone"
                      dataKey={`${metric.type}_${dev.id}_fcst`}
                      name={`${metric.label} forecast\n(${dev.name})`}
                      stroke={fcstColor}
                      strokeWidth={2}
                      dot={{ r: 3, shape: 'diamond' }}
                      connectNulls={true}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function MetricCategoryPanel({ title, categoryKey, metrics, chartData, visibleDevices, forecastVisible = true, localTimeEnabled = true }) {
  // 1. ADDED LOGGING: Log loaded data keys and metrics for inspection
  console.log(`[MetricCategoryPanel] Rendering category "${categoryKey || title}":`, {
    metrics: metrics?.map((m) => m.type || m),
    chartDataRows: chartData?.length || 0,
    sampleKeys: chartData?.[0] ? Object.keys(chartData[0]) : []
  });

  if (metrics.length === 0) return null;

  const isAccumulationCategory = categoryKey === 'crop' || String(title).toLowerCase().includes('accumulation');
  const hasWindComponents = metrics.some((m) => m.type.includes('wind_u') || m.type.includes('wind_v') || m.type === 'wind');

  const filteredMetrics = metrics.filter((m) => !m.type.includes('wind_u') && !m.type.includes('wind_v') && !m.type.includes('growth_stage'));

  return (
    <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#0f172a', textTransform: 'capitalize' }}>
        {title}
      </h3>

      {isAccumulationCategory ? (
        groupAccumulationMetricsBySpecies(metrics).map((speciesGroup, idx) => (
          <AccumulationPhenologyChart
            key={`species-group-${idx}`}
            speciesGroup={speciesGroup}
            chartData={chartData}
            visibleDevices={visibleDevices}
            forecastVisible={forecastVisible}
            localTimeEnabled={localTimeEnabled}
          />
        ))
      ) : (
        <>
          {hasWindComponents && (
            <WindChart
              chartData={chartData}
              visibleDevices={visibleDevices}
              forecastVisible={forecastVisible}
              localTimeEnabled={localTimeEnabled}
            />
          )}

          {sortCategoryMetrics(categoryKey || title, filteredMetrics).map((m) => (
            <StandardLineChart
              key={m.type}
              metric={m}
              chartData={chartData}
              visibleDevices={visibleDevices}
              forecastVisible={forecastVisible}
              localTimeEnabled={localTimeEnabled}
            />
          ))}
        </>
      )}
    </div>
  );
}
